<script setup lang="ts">
import { ArrowUpDown, Search } from 'lucide-vue-next';
import { computed, ref } from 'vue';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import ReferenceFiltersPopover from './ReferenceFiltersPopover.vue';
import type { FiltersState } from './ReferenceFiltersPopover.vue';
import ReferenceListItem from './ReferenceListItem.vue';
import type { ReferenceItem } from './ReferenceListItem.vue';

const props = defineProps<{
    references: ReferenceItem[];
    density?: 'compact' | 'comfortable';
    citedIds?: ReadonlySet<string>;
    collections?: string[];
    /** Wayfinder URL for the manual store endpoint */
    storeUrl?: string;
    projectId?: string;
}>();

const emit = defineEmits<{
    cite: [ref: ReferenceItem];
}>();

// ── Search ─────────────────────────────────────────────────────────────
const search = ref('');

// ── Filters ────────────────────────────────────────────────────────────
const filters = ref<FiltersState>({
    source: null,
    yearFrom: null,
    yearTo: null,
    hasPdf: null,
    citedOnly: null,
    collection: null,
});

const activeFilterCount = computed(() => {
    let n = 0;

    if (filters.value.source) {
        n++;
    }

    if (filters.value.yearFrom) {
        n++;
    }

    if (filters.value.yearTo) {
        n++;
    }

    if (filters.value.hasPdf) {
        n++;
    }

    if (filters.value.citedOnly) {
        n++;
    }

    if (filters.value.collection) {
        n++;
    }

    return n;
});

function applyFilters(newFilters: FiltersState) {
    filters.value = newFilters;
}

// ── Sort ───────────────────────────────────────────────────────────────
type SortKey = 'year_desc' | 'year_asc' | 'title' | 'author';
const sort = ref<SortKey>('year_desc');
const sortOptions: { value: SortKey; label: string }[] = [
    { value: 'year_desc', label: 'Année ↓' },
    { value: 'year_asc', label: 'Année ↑' },
    { value: 'title', label: 'Titre' },
    { value: 'author', label: 'Auteur' },
];

function cycleSort() {
    const idx = sortOptions.findIndex((o) => o.value === sort.value);
    sort.value = sortOptions[(idx + 1) % sortOptions.length].value;
}

const currentSortLabel = computed(
    () => sortOptions.find((o) => o.value === sort.value)?.label ?? '',
);

// ── Citation counts ────────────────────────────────────────────────────
function citedCount(id: string): number {
    if (!props.citedIds) {
        return 0;
    }

    // citedIds is a Set of IDs — one entry per paper (multiple citations = 1 entry in Set)
    return props.citedIds.has(id) ? 1 : 0;
}

// ── Filtered & sorted list ────────────────────────────────────────────
const filtered = computed<ReferenceItem[]>(() => {
    let list = props.references;

    // Text search
    if (search.value.trim()) {
        const q = search.value.toLowerCase().trim();
        list = list.filter((r) => {
            const authors = Array.isArray(r.authors)
                ? r.authors.join(' ')
                : (r.authors ?? '');

            return (
                r.title.toLowerCase().includes(q) ||
                authors.toLowerCase().includes(q) ||
                String(r.year ?? '').includes(q) ||
                (r.abstract ?? '').toLowerCase().includes(q) ||
                (r.doi ?? '').toLowerCase().includes(q)
            );
        });
    }

    // Source filter
    if (filters.value.source) {
        list = list.filter((r) => r.source === filters.value.source);
    }

    // Year range
    if (filters.value.yearFrom) {
        list = list.filter((r) => (r.year ?? 0) >= filters.value.yearFrom!);
    }

    if (filters.value.yearTo) {
        list = list.filter((r) => (r.year ?? 9999) <= filters.value.yearTo!);
    }

    // Has PDF
    if (filters.value.hasPdf) {
        list = list.filter((r) => r.has_pdf);
    }

    // Cited only
    if (filters.value.citedOnly && props.citedIds) {
        list = list.filter((r) => props.citedIds!.has(r.id));
    }

    // Collection
    if (filters.value.collection) {
        list = list.filter((r) =>
            r.collections.includes(filters.value.collection!),
        );
    }

    // Sort
    return [...list].sort((a, b) => {
        switch (sort.value) {
            case 'year_desc':
                return (b.year ?? 0) - (a.year ?? 0);
            case 'year_asc':
                return (a.year ?? 0) - (b.year ?? 0);
            case 'title':
                return a.title.localeCompare(b.title);
            case 'author': {
                const aA = Array.isArray(a.authors)
                    ? (a.authors[0] ?? '')
                    : (a.authors ?? '');
                const bA = Array.isArray(b.authors)
                    ? (b.authors[0] ?? '')
                    : (b.authors ?? '');

                return aA.localeCompare(bA);
            }
        }
    });
});

const isCompact = computed(() => props.density === 'compact');
</script>

<template>
    <div class="flex min-h-0 flex-1 flex-col overflow-hidden">
        <!-- Search + filter bar -->
        <div
            class="shrink-0 border-b border-border/40 bg-background"
            :class="isCompact ? 'space-y-1.5 p-2' : 'space-y-2 p-3'"
        >
            <div class="relative">
                <Search
                    class="absolute top-1/2 left-2.5 -translate-y-1/2 text-muted-foreground"
                    :class="isCompact ? 'h-3 w-3' : 'h-3.5 w-3.5'"
                />
                <Input
                    v-model="search"
                    placeholder="Rechercher…"
                    :class="isCompact ? 'h-7 pl-7 text-xs' : 'h-8 pl-8 text-xs'"
                />
            </div>

            <div class="flex items-center gap-1.5">
                <ReferenceFiltersPopover
                    :collections="collections ?? []"
                    :active-count="activeFilterCount"
                    @change="applyFilters"
                />

                <Button
                    size="sm"
                    variant="ghost"
                    class="h-8 gap-1.5 text-xs"
                    @click="cycleSort"
                >
                    <ArrowUpDown class="h-3 w-3" />
                    {{ currentSortLabel }}
                </Button>

                <span class="ml-auto text-[11px] text-muted-foreground">
                    {{ filtered.length }} / {{ references.length }}
                </span>
            </div>
        </div>

        <!-- List -->
        <ScrollArea class="min-h-0 flex-1">
            <p
                v-if="filtered.length === 0"
                class="px-4 py-8 text-center text-muted-foreground italic"
                :class="isCompact ? 'text-xs' : 'text-sm'"
            >
                {{
                    search || activeFilterCount
                        ? 'Aucune référence ne correspond.'
                        : 'Aucune référence retenue pour ce projet.'
                }}
            </p>

            <ul v-else class="divide-y divide-border/30">
                <ReferenceListItem
                    v-for="ref in filtered"
                    :key="ref.id"
                    :reference="ref"
                    :density="density"
                    :cited-count="citedCount(ref.id)"
                    @cite="emit('cite', $event)"
                />
            </ul>
        </ScrollArea>
    </div>
</template>
